import 'react-international-phone/style.css';
import CheckIcon from '@mui/icons-material/Check';
import {
  BaseTextFieldProps,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone';

interface MUIPhoneProps extends BaseTextFieldProps {
  value: string;
  onChange: (phone: string) => void;
  defaultCountry?: string;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  id?: string;
  name?: string;
  FormHelperTextProps?: any;
}

const countryRules: { [key: string]: { minLength: number; maxLength: number; regex: RegExp } } = {
  es: {
    minLength: 9,
    maxLength: 9,
    regex: /^(6|7|8|9)\d{8}$/,
  },
  us: {
    minLength: 10,
    maxLength: 10,
    regex: /^\d{10}$/,
  },
  fr: {
    minLength: 9,
    maxLength: 9,
    regex: /^(6|7|8|9)\d{8}$/,
  },
};

const MuiPhone: React.FC<MUIPhoneProps> = ({
  value,
  onChange,
  defaultCountry = 'es',
  onBlur,
  error,
  helperText,
  id,
  name,
  disabled,
  FormHelperTextProps,
  ...restProps
}) => {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry,
    value,
    countries: defaultCountries,
    onChange: (data) => {
      onChange(data.phone);
    },
  });

  const [isValid, setIsValid] = useState(true);
  const [errorText, setErrorText] = useState('');

  const validatePhone = (phone: string): boolean => {
    const trimmedPhone = phone.replace(/\D/g, '');
    const rules = countryRules[country.iso2];

    if (rules) {
      const withoutPrefix = trimmedPhone.slice(country.dialCode.length);
      const lengthValid =
        withoutPrefix.length >= rules.minLength && withoutPrefix.length <= rules.maxLength;
      const formatValid = rules.regex.test(withoutPrefix);
      return lengthValid && formatValid;
    }

    return true;
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Solo actualizamos el valor, sin validar en tiempo real
    handlePhoneValueChange(event);
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
    const valid = validatePhone(inputValue);
    setIsValid(valid);
    setErrorText(valid ? '' : 'Teléfono no válido');
  };

  const renderCheckIcon = () => {
    return isValid && inputValue.trim() !== '' ? <CheckIcon color="primary" /> : null;
  };

  return (
    <TextField
      id={id}
      name={name}
      variant="outlined"
      label="Teléfono(*)"
      color="primary"
      placeholder="Número de teléfono"
      value={inputValue}
      onChange={handlePhoneChange}
      onBlur={handleBlur}
      error={!!errorText}
      helperText={errorText || helperText}
      type="tel"
      inputRef={inputRef}
      disabled={disabled}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" style={{ marginRight: '2px', marginLeft: '-8px' }}>
            <Select
              MenuProps={{
                style: {
                  height: '300px',
                  width: '360px',
                  top: '10px',
                  left: '-34px',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
              sx={{
                width: 'max-content',
                fieldset: {
                  display: 'none',
                },
                '&.Mui-focused:has(div[aria-expanded="false"])': {
                  fieldset: {
                    display: 'block',
                  },
                },
                '.MuiSelect-select': {
                  padding: '8px',
                  paddingRight: '24px !important',
                },
                svg: {
                  right: 0,
                },
              }}
              value={country.iso2}
              onChange={(e) => setCountry(e.target.value as CountryIso2)}
              renderValue={(value: string | (string & NonNullable<unknown>) | undefined) => (
                <FlagImage iso2={value} style={{ display: 'flex' }} />
              )}
            >
              {defaultCountries.map((c) => {
                const country = parseCountry(c);
                return (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    <FlagImage iso2={country.iso2} style={{ marginRight: '8px' }} />
                    <Typography marginRight="8px">{country.name}</Typography>
                    <Typography color="gray">+{country.dialCode}</Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </InputAdornment>
        ),
        endAdornment: renderCheckIcon(),
      }}
      FormHelperTextProps={FormHelperTextProps}
      {...restProps}
    />
  );
};

export default MuiPhone;
